import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Shield, Brain, Target, ArrowRight } from 'lucide-react';
import { profileData, companyInfo, services, coreValues } from '../mock';

const Home = () => {
  const [showDefinition, setShowDefinition] = useState(false);

  useEffect(() => {
    // Fade out logo and show definition after 3 seconds
    const fadeTimer = setTimeout(() => {
      setShowDefinition(true);
    }, 3000);

    return () => clearTimeout(fadeTimer);
  }, []);

  const serviceIcons = {
    'ERP Mega Projects': Target,
    'Cybersecurity Assurance': Shield,
    'AI Adoption & Data Assurance': Brain,
    'IT Governance & Oversight': Eye
  };

  return (
    <div className="page-container landing-page">
      {/* Hero Section - Full Screen with Transitions */}
      <section className="hero-section-adaptive">
        {/* Logo - Fades Out */}
        <div className={`hero-logo-container-adaptive ${showDefinition ? 'hidden' : 'visible'}`}>
          <img 
            src={profileData.logo} 
            alt="Videre Scire" 
            className="hero-logo-xlarge"
          />
        </div>

        {/* Definition - Fades In */}
        <div className={`definition-container ${showDefinition ? 'visible' : 'hidden'}`}>
          <div className="meaning-content">
            <div className="meaning-latin-layout">
              <div className="latin-column">
                <span className="latin-word">Videre</span>
                <span className="latin-pronunciation-clear">(vee-DEH-reh)</span>
                <span className="latin-meaning">"To see"</span>
              </div>
              
              <div className="latin-separator-column">
                <span className="latin-separator-large">+</span>
              </div>
              
              <div className="latin-column">
                <span className="latin-word">Scire</span>
                <span className="latin-pronunciation-clear">(SKEE-reh)</span>
                <span className="latin-meaning">"To know"</span>
              </div>
            </div>
            
            <div className="meaning-divider"></div>
            
            <p className="body-large meaning-description">
              Videre Scire is a Latin phrase meaning <strong>"to see in order to know"</strong> or <strong>"knowing through seeing."</strong>
            </p>
            <p className="body-medium meaning-philosophy">
              True knowledge comes from direct observation, insight, and perception. We help boards and executives see clearly into risks, culture, and systems—so decisions are made wisely.
            </p>
          </div>
        </div>
      </section>

      {/* Context Section */}
      <section className="context-section">
        <div className="context-content">
          <p className="body-large context-paragraph">
            Large-scale systems implementations remain among the riskiest investments enterprises make, especially when they are driven by M&A activity. Cost overruns, extended timelines, late discovery of complexity, and weak realization of business value are persistent, structural problems. They are not caused by a lack of technology or capable system integrators (SIs), but by insufficient early truths: internal management politics, lack of detailed process understanding, resistance to change, weak decision-making criteria, and loss of executive control once the project begins.
          </p>
          <p className="body-large context-paragraph-emphasis">
            Our advisory business exists to address exactly these challenges.
          </p>
          <p className="body-large context-paragraph">
            We help boards, executive teams, and CIOs retain control of large-scale systems projects, from first commitment through execution, without becoming a system integrator and without displacing delivery partners. Our role is independent, lifecycle-spanning, and anchored in making complexity visible early, decisions explicit, and execution predictable.
          </p>
        </div>
      </section>

      {/* Vision Section */}
      <section className="content-section">
        <div className="section-header">
          <h2 className="display-medium">Vision</h2>
        </div>
        <p className="body-large">{companyInfo.vision}</p>
      </section>

      {/* Mission Section */}
      <section className="content-section">
        <div className="section-header">
          <h2 className="display-medium">Mission</h2>
        </div>
        <p className="body-large">{companyInfo.mission}</p>
      </section>

      {/* Services Section */}
      <section className="content-section">
        <div className="section-header">
          <h2 className="display-medium">Core Expertise</h2>
        </div>
        <div className="dark-grid">
          {services.map((service, index) => {
            const IconComponent = serviceIcons[service.title] || Target;
            return (
              <div key={index} className="service-card">
                <div className="service-icon">
                  <IconComponent size={32} />
                </div>
                <h3 className="heading-2">{service.title}</h3>
                <p className="body-medium service-description">{service.description}</p>
                <ul className="service-details">
                  {service.details.map((detail, idx) => (
                    <li key={idx} className="body-small">{detail}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* Core Values Section */}
      <section className="content-section">
        <div className="section-header">
          <h2 className="display-medium">Core Values</h2>
        </div>
        <div className="values-grid">
          {coreValues.map((value, index) => (
            <div key={index} className="value-card">
              <h3 className="heading-3">{value.title}</h3>
              <p className="body-medium">{value.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <h2 className="display-large">Ready to see clearly?</h2>
        <p className="body-large">Let's discuss how we can guide your organization through complex digital transformations.</p>
        <Link to="/contact" className="btn-primary">
          Start a Conversation
          <ArrowRight size={20} />
        </Link>
      </section>
    </div>
  );
};

export default Home;
